import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-widget-layout.st.css';
import {DoppeActionTypeWidgetHeader} from '../doppe-action-type-widget-header/doppe-action-type-widget-header';
import {IDoppeViewerActionContext} from '../../doppe-sdk';
import {
    useIsWidgetInFixedHeight,
    useWidgetRenderingContext
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {AnimatedAutoSizedElement, useObservableValue} from '@wix/devzai-utils-react';
import {
    DoppeActionBackHeaderNew
} from '../../doppe-action-types/doppe-actions-common/doppe-action-back-header-new/doppe-action-back-header-new';
import {assertDefined, evaluateFunction} from '@wix/devzai-utils-common';
import {DoppeViewerModalPanelHeader} from '../doppe-viewer-modal-panel-header/doppe-viewer-modal-panel-header';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {FlexLayout} from '@wix/devzai-common-client';

export const DoppeActionTypeWidgetLayout = React.memo(React.forwardRef<HTMLDivElement, DoppeActionTypeWidgetLayout.Props>(function DoppeActionTypeWidgetLayout(props, ref) {
    const {
        children,
        actionContext,
        withBorderBottom = false,
        icon,
        isForm = false,
        forceMaxHeight = false,
        noValidate = false,
        className,
        ...htmlAttributes
    } = props;

    const clientServices = useDoppeViewerClientServices();

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(actionContext.action.actionTypeId),
        `Action type with id '${actionContext.action.actionTypeId}' isn't registered`);

    const {
        hideHeader,
        renderingInnerStateController,
        headerCustomSettings,
        observableCurrentItemIdToRender,
        isFullScreen,
    } = useWidgetRenderingContext();

    const currentItemIdToRender = useObservableValue(observableCurrentItemIdToRender);
    const currentActionListItem = useMemo(() => {
        return currentItemIdToRender ? (actionType.resolveListItems?.(actionContext.action) ?? []).find(item => {
            return item.itemId === currentItemIdToRender;
        }) : undefined
    }, [currentItemIdToRender, actionContext.action.settings]);

    const inInnerNavigation = useObservableValue(renderingInnerStateController);

    const isWidgetInFixedHeight = useIsWidgetInFixedHeight();

    return React.createElement(isForm ? 'form' : 'div', {
        ...htmlAttributes,
        ...(isForm ? {noValidate: noValidate} : {}),
        ref:ref,
        className: style(
                classes.root,
            {
                isFixedHeight: isWidgetInFixedHeight,
                forceMaxHeight: forceMaxHeight,
                isFullScreen: !!isFullScreen,
            },
            className
        ),
        children: (
            <DoppeActionTypeWidgetLayoutContext.Provider
                value={{
                    forceMaxHeight: forceMaxHeight,
                }}
            >
                {
                    evaluateFunction(() => {

                        if (hideHeader) {
                            return null;
                        }

                        if (headerCustomSettings) {
                            return (
                                <DoppeViewerModalPanelHeader
                                    className={style(classes.header, {})}
                                    backButtonCallback={headerCustomSettings.backButtonCallback ? () => {
                                        headerCustomSettings.backButtonCallback?.();
                                    } : undefined}
                                    dismissButtonCallback={headerCustomSettings.dismissButtonCallback}
                                    title={currentActionListItem?.title ?? headerCustomSettings.title}
                                    subtitle={!currentActionListItem?.title ? headerCustomSettings.subtitle : undefined}
                                />
                            )
                        }
                        else if (inInnerNavigation) {
                            return (
                                <DoppeActionBackHeaderNew
                                    onBackClick={() => {
                                        renderingInnerStateController.setValue(false);
                                        observableCurrentItemIdToRender?.setValue(null);
                                    }}
                                    layout={actionContext.action.settings.layout}
                                    title={currentActionListItem?.title ?? actionContext.action.name}
                                />
                            );
                        } else {
                            return (
                                <DoppeActionTypeWidgetHeader
                                    className={style(classes.header, {})}
                                    icon={icon}
                                    actionContext={actionContext}
                                    withBorderBottom={withBorderBottom}
                                />
                            );
                        }
                    })
                }
                <AnimatedAutoSizedElement
                    className={style(classes.animatedElement, {}, FlexLayout.column.default, FlexLayout.fillRemainingVerticalSpace)}
                    durationMilliseconds={!isWidgetInFixedHeight ? 200 : 0}
                >
                    <div
                        className={style(classes.content, {})}

                    >
                        {children}
                    </div>
                </AnimatedAutoSizedElement>
            </DoppeActionTypeWidgetLayoutContext.Provider>
        )
    })
}));

export namespace DoppeActionTypeWidgetLayout {

    export interface Props extends React.HTMLAttributes<any> , DoppeActionTypeWidgetHeader.Props {
        isFixedHeight?: boolean;
        actionContext: IDoppeViewerActionContext<any>;
        isForm?: boolean;
        noValidate?: boolean;
        children: React.ReactElement;
        forceMaxHeight?: boolean;
    }
}

export const DoppeActionTypeWidgetLayoutContext = React.createContext<{
    forceMaxHeight: boolean;
}>({
    forceMaxHeight: false,
});
